import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, X, Menu, User, Home } from "lucide-react";
import { isAdvisor, logout, getUserRole } from "../utils/auth";
import { ADVISOR_MAIN_NAV, ADVISOR_ADMIN_NAV, navSlug } from "../config/advisorNav";

export default function MobileSidebar({ isOpen, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const canManage = isAdvisor();
  const userRole = getUserRole();

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    logout();
    navigate("/advisor/login");
    onClose();
  };

  const mainMenuItems = ADVISOR_MAIN_NAV;
  const adminMenuItems = ADVISOR_ADMIN_NAV;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div className="fixed top-0 left-0 bottom-0 w-[280px] bg-white z-50 overflow-y-auto shadow-2xl animate-in slide-in-from-left duration-300">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-[#C9D4E6] p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/kavachbox-logo-light.png" alt="Kavach Box" className="h-12 object-contain" />
              <div>
                <p className="text-[#3A4557] text-xs">{canManage ? 'Admin' : 'Employee'}</p>
                <p className="text-[#5A6880] text-[10px]">A part of Shree Finserv Group</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[#EDF1F8] text-[#3A4557]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* User Card */}
        <div className="p-4 border-b border-[#C9D4E6]">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-gradient-to-r from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
              <User className="h-6 w-6 text-[#0D1A3A]" />
            </div>
            <div>
              <p className="text-[#1B2A4A] font-medium">
                {canManage ? 'Prashant K Uunadkat' : 'Operations Assistant'}
              </p>
              <p className="text-[#3A4557] text-sm">
                {canManage ? 'Beshak Verified Advisor' : 'Team Member'}
              </p>
            </div>
          </div>
        </div>

        {/* Main Menu */}
        <div className="p-3">
          <p className="text-[#3A4557] text-xs font-semibold uppercase tracking-wider px-3 mb-2">
            Main Menu
          </p>
          <div className="space-y-1">
            {mainMenuItems.map((item) => {
              const isActive = currentPath === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigate(item.path)}
                  data-testid={`mobile-nav-${navSlug(item.path)}`}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                    isActive 
                      ? 'bg-[#D4A017]/15 text-[#A87608]' 
                      : 'text-[#2E3B57] hover:bg-[#EDF1F8]'
                  }`}
                >
                  <item.icon className={`h-5 w-5 ${isActive ? 'text-[#A87608]' : item.color}`} />
                  <span className="font-medium">{item.label}</span>
                  {isActive && (
                    <div className="ml-auto w-2 h-2 bg-[#D4A017] rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Admin Features - Only for Admin */}
        {canManage && (
          <div className="p-3 border-t border-[#C9D4E6]">
            <p className="text-[#3A4557] text-xs font-semibold uppercase tracking-wider px-3 mb-2">
              Admin Features
            </p>
            <div className="space-y-1">
              {adminMenuItems.map((item) => {
                const isActive = currentPath === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavigate(item.path)}
                    data-testid={`mobile-nav-${navSlug(item.path)}`}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                      isActive 
                        ? 'bg-[#D4A017]/15 text-[#A87608]' 
                        : 'text-[#2E3B57] hover:bg-[#EDF1F8]'
                    }`}
                  >
                    <item.icon className={`h-5 w-5 ${isActive ? 'text-[#A87608]' : item.color}`} />
                    <span className="font-medium">{item.label}</span>
                    {isActive && (
                      <div className="ml-auto w-2 h-2 bg-[#D4A017] rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Account Section */}
        <div className="p-3 border-t border-[#C9D4E6]">
          <p className="text-[#3A4557] text-xs font-semibold uppercase tracking-wider px-3 mb-2">
            Account
          </p>
          <button
            onClick={() => handleNavigate("/")}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[#2E3B57] hover:bg-[#EDF1F8] transition-all"
          >
            <Home className="h-5 w-5 text-[#3A4557]" />
            <span className="font-medium">Back to Website</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-red-600 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="h-5 w-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#C9D4E6] mt-auto">
          <p className="text-[#3A4557] text-xs text-center">
            Kavach Box • v2.0
          </p>
        </div>
      </div>
    </>
  );
}

// Export Menu Button for use in headers
export function MobileMenuButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="md:hidden p-2 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] transition-colors"
      aria-label="Open menu"
    >
      <Menu className="h-5 w-5 text-[#1B2A4A]" />
    </button>
  );
}
